import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import dayjs from 'dayjs';

import AuthService from '@auth/AuthService';
import { getResolvedDrumkitPortalBaseUrlFromSession } from '@utils/drumkitHostResolution';
import isProd from '@utils/isProd';

import { ExpiredEmailWarning } from 'components/ExpiredEmailWarning';
import { TitlebarButtons } from 'components/Titlebar';
import { Flex } from 'components/layout';
import SidebarLoader from 'components/loading/SidebarLoader';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { SidebarViewContext } from 'contexts/sidebarViewContext';
import { useAuth } from 'hooks/useAuth';
import { useServiceFeatures } from 'hooks/useServiceContext';
import getEmail from 'lib/api/getEmail';
import LoadsSidebar from 'pages/LoadView/LoadsSidebar';
import QuoteSidebar from 'pages/QuoteView/QuoteSidebar';
import { Email } from 'types/Email';
import { initialIngestionMessage } from 'types/IngestionMessage';
import { Maybe } from 'types/UtilityTypes';
import SidebarView from 'types/enums/SidebarView';
import { getViewBasedOnEmail } from 'utils/getViewBasedOnEmail';

import { LoadSearchProvider } from '../alexandria/contexts/loadSearchContext';
import { useOfficeContext } from './OfficeContext';

export default function SidebarOutlookWrapper() {
  const [email, setEmail] = useState<Maybe<Email>>(null);
  // Bumped to look the email up again once Drumkit finishes processing it
  const [emailLookup, setEmailLookup] = useState(0);
  const ingestionMessage = initialIngestionMessage;

  const navigate = useNavigate();
  const { user } = useAuth();
  const [drumkitPortalUrl, setDrumkitPortalUrl] = useState<string>(() =>
    getResolvedDrumkitPortalBaseUrlFromSession(user?.email ?? null)
  );

  const { state, isSharedMailboxEmail: officeIsSharedMailboxEmail } =
    useOfficeContext();
  const { currentView, setCurrentView } = useContext(SidebarViewContext);
  const {
    currentState: { openExternalUrl },
    setCurrentState,
  } = useContext(SidebarStateContext);

  if (!isProd() && state?.mailbox?.item) {
    const item = state.mailbox.item!;
    ingestionMessage.messageID = item.itemId!;
    ingestionMessage.fromName = item.from.displayName;
    ingestionMessage.fromEmail = item.from.emailAddress;
    ingestionMessage.toName = item.to[0]?.displayName;
    ingestionMessage.toEmail = item.to[0]?.emailAddress;
    item.body.getAsync('text', ({ status, value }) => {
      if (status === Office.AsyncResultStatus.Succeeded) {
        ingestionMessage.body = value;
      }
    });
    ingestionMessage.subject = item.subject;
    ingestionMessage.date = dayjs(item.dateTimeCreated?.toString()).format(
      'MMM D, YYYY, h:mm A'
    );
  }

  // Clear email when the Outlook conversation changes so we do not show the previous
  // thread's sidebar (e.g. getEmail 404 for un-ingested threads would return the previous thread's email)
  useEffect(() => {
    setEmail(null);
    setCurrentState((prev) => ({
      ...prev,
      isSharedMailboxEmail: officeIsSharedMailboxEmail,
    }));

    if (!state?.mailbox?.item?.conversationId) {
      return;
    }

    // Cancel updates from previous conversation data if a new conversation is started
    let isCancelled = false;
    const currentConversationId = state.mailbox.item.conversationId;

    getEmail(currentConversationId, ingestionMessage).then((result) => {
      if (!isCancelled && result.isOk()) {
        const emailData = result.value;
        setEmail(emailData);
        setCurrentState((prev) => ({
          ...prev,
          isSharedMailboxEmail:
            emailData?.isSharedMailboxUser ?? prev.isSharedMailboxEmail,
        }));
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [
    emailLookup,
    officeIsSharedMailboxEmail,
    state?.mailbox?.item?.conversationId,
  ]);

  const {
    isLoading: isServiceFeaturesLoading,
    serviceFeaturesEnabled,
    needsInboxReauthorization,
    emailProvider,
  } = useServiceFeatures();

  useEffect(() => {
    if (email?.customPortalDomain) {
      setDrumkitPortalUrl(email.customPortalDomain);
      return;
    }

    setDrumkitPortalUrl(
      getResolvedDrumkitPortalBaseUrlFromSession(user?.email ?? null)
    );
  }, [email?.customPortalDomain, user?.email]);

  useEffect(() => {
    // Skip getting/setting default view if service features are still loading
    if (isServiceFeaturesLoading) {
      return;
    }

    const defaultView = getViewBasedOnEmail(serviceFeaturesEnabled);

    if (currentView !== defaultView) {
      setCurrentView(defaultView);
    }
  }, [email, serviceFeaturesEnabled]);

  if (isServiceFeaturesLoading) {
    return <SidebarLoader />;
  }

  // If switching extension environments, token signature will be
  // invalidated. Use logout to start fresh.
  const outlookLogout = () => {
    AuthService.logOut();
    navigate('/login');
  };

  return (
    <LoadSearchProvider
      initialFreightTrackingIDs={email?.freightTrackingIDs || []}
    >
      <Flex
        direction='col'
        className='py-2'
        style={{ minHeight: 'calc(100vh - 44px)' }}
      >
        <Flex justify='end' gap='sm' className='px-3 z- w-full'>
          <TitlebarButtons
            outlookLogout={outlookLogout}
            drumkitPortalUrl={drumkitPortalUrl}
          />
        </Flex>

        {needsInboxReauthorization && (
          <ExpiredEmailWarning
            emailProvider={emailProvider}
            openExternalUrl={openExternalUrl}
            drumkitPortalUrl={drumkitPortalUrl}
          />
        )}

        {currentView === SidebarView.Quote ? (
          <QuoteSidebar
            email={email}
            onEmailProcessed={() => setEmailLookup((count) => count + 1)}
          />
        ) : (
          currentView === SidebarView.Loads && <LoadsSidebar email={email} />
        )}
      </Flex>
    </LoadSearchProvider>
  );
}
